import { install, gtag, generateStringifyQueryParams } from '../helpers/GoogleAnalytic';
import Core from './SystemsCore';
import { ITrackNewAnalytics, IPurchaseNew, PurchaseData, EventDataTrack } from '../Interfaces/Events';
import { A_B_TEST_NAME } from '../enums';
import { logger } from '@user-portal/logger';

export default class GoogleAnalytics extends Core {
  private Google: typeof gtag;
  private state: {
    isInit: boolean;
    anonymize: boolean;
    googleAdsId: string | null;
    googleAdsShopId: string | null;
  };
  public name: string;

  constructor() {
    super();
    this.name = 'ga';
    this.Google = gtag;
    this.state = {
      isInit: false,
      anonymize: false,
      googleAdsId: null,
      googleAdsShopId: null,
    };
  }

  init({ id, config }: { id: string; config: { googleAdsId?: string; googleAdsShopId?: string; domains?: string[] } }) {
    try {
      install(id, config);

      if (config?.googleAdsId && config?.googleAdsShopId) {
        this.state.googleAdsId = config?.googleAdsId;
        this.state.googleAdsShopId = config?.googleAdsShopId;
      }

      this.state.isInit = true;
    } catch (error) {
      logger.error('Google init ', { error });
    }
  }

  getInit() {
    return this.state.isInit;
  }

  getABTestName() {
    return A_B_TEST_NAME;
  }

  async track(eventName: string, eventData: EventDataTrack | PurchaseData = {}) {
    const eventLabel = eventData?.event_label;
    const eventAction = eventData?.event_action;

    const urlParams = await generateStringifyQueryParams();

    this.Google('event', 'page load', {
      event_category: eventName,
      dimension1: this.getABTestName(),
      value: 1,
      ...(eventLabel && { event_label: eventLabel }),
      ...(eventAction && { event_action: eventAction }),
      ...(urlParams && {
        page_location: `${window.location.href}/?${urlParams}`,
      }),
    });
  }

  async trackNew({ eventName, eventAction, eventData = {} }: ITrackNewAnalytics): Promise<void> {
    const eventLabel = eventData?.event_label;
    const urlParams = await generateStringifyQueryParams();

    this.Google('event', 'page load', {
      event_category: eventName,
      dimension1: this.getABTestName(),
      value: 1,
      ...(eventAction && { event_action: eventAction }),
      ...(eventLabel && { event_label: eventLabel }),
      ...(urlParams && {
        page_location: `${window.location.href}/?${urlParams}`,
      }),
    });
  }

  async purchase(event_name: string, data: PurchaseData) {
    logger.info('GA', { event_name });
    const { value, currency, order_id, ab_test_name, content_id, payment } = data;
    const urlParams = await generateStringifyQueryParams();

    this.Google('event', event_name, {
      transaction_id: order_id,
      value,
      currency,
      ...(ab_test_name && { dimension1: ab_test_name }),
      ...(urlParams && {
        page_location: `${window.location.href}/?${urlParams}`,
      }),
      items: [
        {
          name: content_id,
          category: payment,
          quantity: 1,
          price: value,
          payment,
        },
      ],
    });

    if (this.state.googleAdsId && this.state.googleAdsShopId) {
      this.Google('event', 'conversion', {
        send_to: `${this.state.googleAdsId}/${this.state.googleAdsShopId}`,
        transaction_id: order_id,
        value,
        currency,
        ...(urlParams && {
          page_location: `${window.location.href}/?${urlParams}`,
        }),
      });
    }
  }

  async purchaseNew({ eventName, data }: IPurchaseNew): Promise<void> {
    const { value, currency, order_id, ab_test_name, content_id, payment } = data;

    const urlParams = await generateStringifyQueryParams();

    this.Google('event', eventName, {
      transaction_id: order_id,
      value,
      currency,
      items: [
        {
          name: content_id,
          category: payment,
          quantity: 1,
          price: value,
          payment,
        },
      ],
      ...(ab_test_name && { dimension1: ab_test_name }),
      ...(urlParams && {
        page_location: `${window.location.href}/?${urlParams}`,
      }),
    });

    if (this.state.googleAdsId && this.state.googleAdsShopId) {
      this.Google('event', 'conversion', {
        send_to: `${this.state.googleAdsId}/${this.state.googleAdsShopId}`,
        transaction_id: order_id,
        value,
        currency,
        ...(urlParams && {
          page_location: `${window.location.href}/?${urlParams}`,
        }),
      });
    }
  }
}
